import { find, includes } from 'lodash';

/**
 * Service module for DeviceIpSelector business logic.
 * Handles IP option building, interface mapping, and collector name extraction.
 */

const AUTOMATIC_VALUE = '';
export const AUTOMATIC_TYPE = 'auto';
export const MANUAL_TYPE = 'manual';
const SOFTWARE_LOOPBACK = 'Loopback';

/**
 * Builds a lookup map from IP address to interface object.
 * Handles the API shape where each interface has an ipAddresses[] array.
 * @param {Array} interfaceList - Device interfaces from API
 * @returns {Object} Map of IP address string → interface object
 */
export const buildInterfaceMap = (interfaceList) => {
    const map = {};
    interfaceList.forEach((iface) => {
        if (Array.isArray(iface.ipAddresses)) {
            iface.ipAddresses.forEach((ip) => {
                map[ip] = iface;
            });
        }
    });
    return map;
};

/**
 * Extracts the collector name from an IP metadata object.
 * Handles both API field naming conventions (plcName and name).
 */
export const getCollectorName = (meta) => meta?.plcName || meta?.name || '';

/**
 * Merges IP metadata (from collectors API) with interface data
 * to build the selectable IP options list.
 *
 * Matches portal/ui mergeData() + buildAutoSelectedIp() behavior:
 * - When hasDeviceOverride is true, returns only manual IP options (no Automatic)
 * - When hasDeviceOverride is false, prepends Automatic option before all manual options
 * - Never filters out manual options (even when IP matches default)
 *
 * @param {string} defaultIp - Device's current monitoring IP
 * @param {Array} metadata - IP address metadata from collectors API
 * @param {Array} interfaces - Device interfaces
 * @param {Array} loopbackIps - Known loopback IPs for this device
 * @param {boolean} hasDeviceOverride - Whether user previously selected a manual IP
 * @returns {Array<{type, value, ipAddress, interfaceType, collectorName, discovered}>}
 */
export const buildSelectableIps = (defaultIp, metadata, interfaces, loopbackIps, hasDeviceOverride) => {
    const metadataList = Array.isArray(metadata) ? metadata : [];
    if (metadataList.length === 0) {
        return [];
    }

    const loopbacks = Array.isArray(loopbackIps) ? loopbackIps : [];

    const interfaceList = Array.isArray(interfaces) ? interfaces : [];
    const ifaceMap = buildInterfaceMap(interfaceList);

    const manualOptions = metadataList.map((meta) => {
        const ip = meta.ipAddress;
        const iface = ifaceMap[ip];
        let ifType = iface?.ifType || '';
        if (!ifType && includes(loopbacks, ip)) {
            ifType = SOFTWARE_LOOPBACK;
        }

        return {
            type: MANUAL_TYPE,
            value: ip,
            ipAddress: ip,
            interfaceType: ifType,
            collectorName: getCollectorName(meta),
            discovered: meta.discovered,
        };
    });

    // When user previously selected a manual IP, hide Automatic option
    // (portal/ui: hasDeviceOverride ? selIps : [autoOption, ...selIps])
    if (hasDeviceOverride) {
        return manualOptions;
    }

    // Build Automatic option, enriched with metadata from matching manual entry
    const autoMatch = find(manualOptions, { ipAddress: defaultIp });
    const autoOption = {
        type: AUTOMATIC_TYPE,
        value: AUTOMATIC_VALUE,
        ipAddress: defaultIp,
        interfaceType: autoMatch?.interfaceType || '',
        collectorName: autoMatch?.collectorName || '',
    };

    return [autoOption, ...manualOptions];
};
